import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Dashboard from '../Pages/Dashboard/Dashboard';
import './Apps.css';
import Cookies from 'js-cookie';

/**
 * Apps Navigation Page
 * Shows all ESG Fit applications user has access to
 * Pattern from LMS
 */
function AppsContent() {
  const { user } = useAuth();
  const [enabledApps, setEnabledApps] = useState([]);

  useEffect(() => {
    // Read access flags from cookies
    const brsrAccess = Cookies.get('brsrAccess') === 'true';
    const complianceAccess = Cookies.get('complianceAccess') === 'true';
    const surveyAccess = Cookies.get('surveyAccess') === 'true';
    const lmsAccess = Cookies.get('lmsAccess') === 'true';

    console.log('🔍 CRM Apps - Access flags:', {
      brsrAccess,
      complianceAccess,
      surveyAccess,
      lmsAccess,
    });

    // Get user role for Survey Dashboard routing
    let userRole = null;
    try {
      const userCookie = Cookies.get('user');
      if (userCookie) {
        const userData = JSON.parse(userCookie);
        userRole = userData.role;
        console.log('🔍 CRM Apps - User role:', userRole);
      }
    } catch (error) {
      console.error('Error parsing user cookie:', error);
    }

    // Check if user is SUPER_ADMIN for Survey routing
    const isSuperAdmin = 
      userRole === 'SUPER_ADMIN' || 
      userRole === 'SUPERADMIN' ||
      (typeof userRole === 'object' && userRole !== null && 
        (userRole.name === 'SUPER_ADMIN' || 
         userRole.name === 'SUPERADMIN' || 
         userRole.accessLevel === 'SUPER_ADMIN'));

    // All available apps (excluding CRM since we're currently in CRM)
    const allApps = [
      {
        name: 'BRSR Tool',
        url: process.env.REACT_APP_BRSR_URL || 'http://localhost:3000',
        icon: '📋',
        color: '#007bff',
        description: 'Business Responsibility & Sustainability Reporting',
        enabled: brsrAccess,
      },
      {
        name: 'Compliance Tool',
        url: process.env.REACT_APP_COMPLIANCE_URL || 'http://localhost:3004',
        icon: '✅',
        color: '#dc3545',
        description: 'Track and manage regulatory compliance requirements',
        enabled: complianceAccess,
      },
      {
        name: 'Survey Dashboard',
        // Role-based routing: SUPER_ADMIN → root, USER → /user
        url: isSuperAdmin 
          ? (process.env.REACT_APP_SURVEY_URL || 'http://localhost:3001')
          : (process.env.REACT_APP_SURVEY_URL || 'http://localhost:3001') + '/user',
        icon: '📊',
        color: '#28a745',
        description: 'Survey Management & Analytics',
        enabled: surveyAccess,
      },
      {
        name: 'LMS',
        url: process.env.REACT_APP_LMS_URL || 'http://localhost:3002',
        icon: '🎓',
        color: '#ffc107',
        description: 'Learning Management System',
        enabled: lmsAccess,
      },
    ];

    const filtered = allApps.filter(app => app.enabled);
    console.log('🔍 CRM Apps - Enabled apps:', filtered);
    setEnabledApps(filtered);
  }, [user]);

  return (
    <div style={{
      padding: "20px",
      background: "#f8f9fa",
      minHeight: "100vh",
      width: "100%"
    }}>
      {/* Modern Header */}
      <div style={{
        background: "white",
        padding: "24px",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        marginBottom: "24px",
        textAlign: "center"
      }}>
        <h1 style={{
          margin: 0,
          fontSize: "28px",
          fontWeight: "700",
          color: "#2c3e50",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px"
        }}>
          <i className="las la-th-large" style={{ color: "#3bac75" }} />
          ESG Fit Applications
        </h1>
        <p style={{
          margin: "8px 0 0 0",
          color: "#6c757d",
          fontSize: "16px"
        }}>
          Access all your ESG management tools in one place
        </p>
      </div>

      {/* Apps Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        gap: "24px",
        maxWidth: "1200px",
        margin: "0 auto"
      }}>
        {enabledApps.map(app => (
          <a
            key={app.name}
            href={app.url}
            target={app.active ? '_self' : '_blank'}
            rel="noopener noreferrer"
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              textDecoration: "none",
              color: "inherit",
              transition: "all 0.3s ease",
              border: app.active ? `3px solid ${app.color}` : "1px solid #dee2e6",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
            }}
          >
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "40px",
              marginBottom: "20px",
              backgroundColor: app.color,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
              {app.icon}
            </div>
            <div>
              <h3 style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#212529"
              }}>
                {app.name}
              </h3>
              <p style={{
                fontSize: "14px",
                color: "#6c757d",
                lineHeight: "1.6",
                margin: 0
              }}>
                {app.description}
              </p>
            </div>
            {app.active && (
              <div style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                color: "white",
                padding: "6px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: app.color
              }}>
                <i className="las la-check" />
                <span>Current</span>
              </div>
            )}
            {!app.active && (
              <div style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                color: "#6c757d",
                fontSize: "20px",
                opacity: 0,
                transition: "opacity 0.2s"
              }}>
                <i className="las la-external-link-alt" />
              </div>
            )}
          </a>
        ))}
      </div>

      {enabledApps.length === 0 && (
        <div style={{
          textAlign: "center",
          padding: "60px 20px",
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        }}>
          <i className="las la-info-circle" style={{ 
            fontSize: "48px", 
            color: "#6c757d",
            marginBottom: "16px",
            display: "block"
          }} />
          <p style={{ fontSize: "16px", color: "#6c757d", margin: 0 }}>
            No applications available. Please contact your administrator.
          </p>
        </div>
      )}
    </div>
  );
}

export default function Apps() {
  return <Dashboard mainComponent={<AppsContent />} />;
}


